/**
 * Mock Employee API Service
 * This service provides dummy employee data for design checking and development
 * Replace with actual API calls when backend is ready
 */

import { generateDummyEmployees, generateDummyStats } from '../utils/dummyData';

// Mock delay to simulate API calls
const delay = (ms = 500) => new Promise(resolve => setTimeout(resolve, ms));

// Generate dummy data once
const dummyEmployees = generateDummyEmployees(50);
const dummyStats = generateDummyStats(dummyEmployees);

// Mock API responses
export const mockEmployeeApi = {
  // Get all employees with pagination and filters
  getEmployees: async (params = {}) => {
    await delay();

    let filteredEmployees = [...dummyEmployees];

    // Apply search filter
    if (params.search) {
      const searchTerm = params.search.toLowerCase();
      filteredEmployees = filteredEmployees.filter(employee =>
        employee.firstName.toLowerCase().includes(searchTerm) ||
        employee.lastName.toLowerCase().includes(searchTerm) ||
        employee.email.toLowerCase().includes(searchTerm) ||
        employee.employeeId.toLowerCase().includes(searchTerm) ||
        employee.department.toLowerCase().includes(searchTerm) ||
        employee.position.toLowerCase().includes(searchTerm)
      );
    }

    // Apply department filter
    if (params.department) {
      filteredEmployees = filteredEmployees.filter(employee =>
        employee.department === params.department
      );
    }

    // Apply status filter
    if (params.status) {
      filteredEmployees = filteredEmployees.filter(employee =>
        employee.status === params.status
      );
    }

    // Apply employment type filter
    if (params.employmentType) {
      filteredEmployees = filteredEmployees.filter(employee =>
        employee.employmentType === params.employmentType
      );
    }

    // Apply hire date filters
    if (params.hireDateFrom) {
      filteredEmployees = filteredEmployees.filter(employee =>
        new Date(employee.hireDate) >= new Date(params.hireDateFrom)
      );
    }

    if (params.hireDateTo) {
      filteredEmployees = filteredEmployees.filter(employee =>
        new Date(employee.hireDate) <= new Date(params.hireDateTo)
      );
    }

    // Apply sorting
    if (params.sortBy) {
      filteredEmployees.sort((a, b) => {
        let aValue = a[params.sortBy];
        let bValue = b[params.sortBy];

        // Handle nested properties
        if (params.sortBy === 'name') {
          aValue = `${a.firstName} ${a.lastName}`;
          bValue = `${b.firstName} ${b.lastName}`;
        }

        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (params.sortOrder === 'desc') {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        } else {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        }
      });
    }

    // Apply pagination
    const limit = params.limit || 10;
    const page = params.page || 1;
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedEmployees = filteredEmployees.slice(startIndex, endIndex);

    return {
      employees: paginatedEmployees,
      pagination: {
        page,
        limit,
        total: filteredEmployees.length,
        totalPages: Math.ceil(filteredEmployees.length / limit)
      },
      stats: dummyStats
    };
  },

  // Get single employee by ID
  getEmployee: async (id) => {
    await delay();
    const employee = dummyEmployees.find(emp => emp._id === id || emp.employeeId === id);

    if (!employee) {
      throw new Error('Employee not found');
    }

    return {
      employee,
      success: true
    };
  },

  // Create new employee
  createEmployee: async (employeeData) => {
    await delay();

    const newEmployee = {
      ...employeeData,
      _id: String(dummyEmployees.length + 1),
      employeeId: `EMP${String(dummyEmployees.length + 1).padStart(4, '0')}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    dummyEmployees.push(newEmployee);

    return {
      employee: newEmployee,
      success: true,
      message: 'Employee created successfully'
    };
  },

  // Update employee
  updateEmployee: async (id, employeeData) => {
    await delay();

    const employeeIndex = dummyEmployees.findIndex(emp => emp._id === id);
    if (employeeIndex === -1) {
      throw new Error('Employee not found');
    }

    const updatedEmployee = {
      ...dummyEmployees[employeeIndex],
      ...employeeData,
      _id: id,
      updatedAt: new Date().toISOString()
    };

    dummyEmployees[employeeIndex] = updatedEmployee;

    return {
      employee: updatedEmployee,
      success: true,
      message: 'Employee updated successfully'
    };
  },

  // Delete employee
  deleteEmployee: async (id) => {
    await delay();

    const employeeIndex = dummyEmployees.findIndex(emp => emp._id === id);
    if (employeeIndex === -1) {
      throw new Error('Employee not found');
    }

    const deletedEmployee = dummyEmployees.splice(employeeIndex, 1)[0];

    return {
      employee: deletedEmployee,
      success: true,
      message: 'Employee deleted successfully'
    };
  },

  // Get employee statistics
  getStats: async () => {
    await delay();
    return {
      stats: generateDummyStats(dummyEmployees),
      success: true
    };
  }
};

// Export dummy data for direct use
export { dummyEmployees, dummyStats };
